import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import { MessageSquare, Mail, Phone, Calendar, MapPin, Square, CreditCard, Filter } from 'lucide-react';
import '../components/styles.css';

export default function ContactRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'new', 'contacted', 'qualified', 'converted', 'closed'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'basic', 'ads'

  useEffect(() => {
    const checkAuth = async () => {
      const supabase = createClient();
      const { data: { user: currentUser } } = await supabase.auth.getUser();

      if (!currentUser) {
        navigate('/crm/login');
        return;
      }

      fetchRequests();
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, filter, typeFilter]);

  const fetchRequests = async () => {
    const supabase = createClient();
    
    let query = supabase
      .from('contact_requests')
      .select('*')
      .order('created_at', { ascending: false });

    if (filter !== 'all') {
      query = query.eq('status', filter);
    }

    if (typeFilter !== 'all') {
      query = query.eq('type', typeFilter);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
    } else {
      setRequests(data || []);
    }
    setLoading(false);
  };

  const updateStatus = async (id, newStatus) => {
    const supabase = createClient();
    const { error } = await supabase
      .from('contact_requests')
      .update({ status: newStatus })
      .eq('id', id);

    if (error) {
      console.error('Erreur lors de la mise à jour:', error);
    } else {
      fetchRequests();
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      qualified: 'bg-purple-100 text-purple-800',
      converted: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || colors.new;
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'Nouveau',
      contacted: 'Contacté',
      qualified: 'Qualifié',
      converted: 'Converti',
      closed: 'Fermé',
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-lg">Chargement...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Demandes de contact</h1>
            <p className="mt-2 text-gray-600">
              Gérez toutes les demandes de contact et publicitaires
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="new">Nouveau</option>
              <option value="contacted">Contacté</option>
              <option value="qualified">Qualifié</option>
              <option value="converted">Converti</option>
              <option value="closed">Fermé</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Tous les types</option>
              <option value="basic">Contact basique</option>
              <option value="ads">Demande de devis</option>
            </select>

            <div className="ml-auto text-sm text-gray-600">
              {requests.length} demande{requests.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Liste des demandes */}
        <div className="space-y-4">
          {requests.length === 0 ? (
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucune demande trouvée</p>
            </div>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {request.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {request.email}
                          </span>
                          {request.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-4 w-4" />
                              {request.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(request.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {getStatusLabel(request.status)}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          request.type === 'ads' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {request.type === 'ads' ? 'Devis' : 'Contact'}
                        </span>
                      </div>
                    </div>

                    {request.type === 'ads' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 p-3 bg-purple-50 rounded-lg">
                        {request.address && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-purple-600" />
                            <span className="text-gray-700">{request.address}</span>
                          </div>
                        )}
                        {request.surface && (
                          <div className="flex items-center gap-2 text-sm">
                            <Square className="h-4 w-4 text-purple-600" />
                            <span className="text-gray-700">{request.surface} m²</span>
                          </div>
                        )}
                        {request.credit_impot && (
                          <div className="flex items-center gap-2 text-sm">
                            <CreditCard className="h-4 w-4 text-purple-600" />
                            <span className="text-gray-700">
                              Crédit d'impôt: {request.credit_impot === 'oui' ? 'Oui' : 'Non'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {request.service && (
                      <div className="mb-3">
                        <span className="text-sm text-gray-600">Service: </span>
                        <span className="text-sm font-medium text-gray-900">{request.service}</span>
                      </div>
                    )}

                    <div className="mb-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{request.message}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 lg:w-48">
                    <select
                      value={request.status}
                      onChange={(e) => updateStatus(request.id, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="new">Nouveau</option>
                      <option value="contacted">Contacté</option>
                      <option value="qualified">Qualifié</option>
                      <option value="converted">Converti</option>
                      <option value="closed">Fermé</option>
                    </select>
                    <a
                      href={`mailto:${request.email}`}
                      className="px-3 py-2 bg-primary-600 text-white rounded-lg text-sm text-center hover:bg-primary-700 transition-colors"
                    >
                      Répondre
                    </a>
                    {request.phone && (
                      <a
                        href={`tel:${request.phone}`}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm text-center hover:bg-gray-200 transition-colors"
                      >
                        Appeler
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

